import React, { useMemo } from 'react';
import { View, StyleSheet, Dimensions, Platform } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
  runOnJS,
} from 'react-native-reanimated';
import { useMenu } from '../../navigation/MenuContext';

const { width: SCREEN_WIDTH } = Dimensions.get('window');
const EDGE_WIDTH = 25; // Width of the edge detection area
const SWIPE_THRESHOLD = 50; // Minimum distance to trigger menu open

// Main pages where left-edge swipe should open the menu instead of navigating back
export const MAIN_PAGES = [
  'WelcomeScreen',
  'ChargerSelection',
  'TransactionHistory',
  'MyChargers',
  'HostInsights',
  'PartnerPortal',
  'PartnerEarningsDashboard',
  'PartnerInsights',
  'AdminScreen',
  'OnboardingStatus',
  'RevenueSplitDashboard',
  'SplitPartnerships',
];

interface EdgeSwipeGestureHandlerProps {
  currentRouteName?: string;
}

const EdgeSwipeGestureHandler: React.FC<EdgeSwipeGestureHandlerProps> = ({
  currentRouteName,
}) => {
  const { openMenu, isMenuOpen } = useMenu();
  const translateX = useSharedValue(0);
  const isActive = useSharedValue(false);

  // Only enable edge swipe on main pages
  const isMainPage = currentRouteName ? MAIN_PAGES.includes(currentRouteName) : false;
  const shouldRender = Platform.OS !== 'web' && isMainPage && !isMenuOpen;

  // Create gesture - must be created unconditionally for hooks rules
  const panGesture = useMemo(() => {
    return Gesture.Pan()
      .activeOffsetX([5, SCREEN_WIDTH]) // Only activate on rightward swipes
      .failOffsetY([-20, 20]) // Fail if vertical movement is too large
      .onStart((event) => {
        // Only start if the touch began near the left edge
        if (event.x < EDGE_WIDTH) {
          isActive.value = true;
        }
      })
      .onUpdate((event) => {
        if (isActive.value) {
          translateX.value = Math.max(0, event.translationX);
        }
      })
      .onEnd((event) => {
        if (isActive.value && event.translationX > SWIPE_THRESHOLD) {
          // Swipe was far enough, open the menu
          runOnJS(openMenu)();
        }
        // Reset
        translateX.value = withSpring(0);
        isActive.value = false;
      })
      .onFinalize(() => {
        translateX.value = withSpring(0);
        isActive.value = false;
      });
  }, [openMenu, translateX, isActive]);

  const animatedStyle = useAnimatedStyle(() => ({
    opacity: isActive.value ? 0.3 : 0,
    transform: [{ translateX: Math.min(translateX.value * 0.5, 50) }],
  }));

  // Conditionally render after all hooks have been called
  if (!shouldRender) {
    return null;
  }

  return (
    <GestureDetector gesture={panGesture}>
      <View style={styles.edgeContainer} pointerEvents="box-none">
        <Animated.View style={[styles.indicator, animatedStyle]} />
      </View>
    </GestureDetector>
  );
};

const styles = StyleSheet.create({
  edgeContainer: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: EDGE_WIDTH,
    zIndex: 100,
  },
  indicator: {
    position: 'absolute',
    left: 0,
    top: '30%',
    height: '40%',
    width: 4,
    backgroundColor: '#39BEB7',
    borderTopRightRadius: 4,
    borderBottomRightRadius: 4,
  },
});

export default EdgeSwipeGestureHandler;
